import {Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {EndOfAssignmentEventType, TrackerOrderStatus} from '../../model';
import {camelCaseToDashCase, getDateInPreviousQuarter} from '../../utils';
import {AppStateService} from '../shared/app-state.service';
import {NameFormatPipe} from '../shared/name-format.pipe';
import {environment} from '../../environments/environment';
import * as max from 'date-fns/max';
import * as compareAsc from 'date-fns/compare_asc';
import * as compareDesc from 'date-fns/compare_desc';
import * as isSameQuarter from 'date-fns/is_same_quarter';
import * as differenceInDays from 'date-fns/difference_in_days';
import * as startOfToday from 'date-fns/start_of_today';
import * as endOfToday from 'date-fns/end_of_today';
import * as isBefore from 'date-fns/is_before';

export enum PositionStatus {
  Undefined = 'undefined',
  Open = 'open',
  Working = 'working',
  Completed = 'completed',
  Closed = 'closed'
}

export function getPositionStatus(order: Order): PositionStatus {
  switch (order.positionStatus) {
    case 'undefined':
      return PositionStatus.Undefined;
    case 'open':
      return PositionStatus.Open;
    case 'working':
      return PositionStatus.Working;
    case 'completed':
      return PositionStatus.Completed;
    case 'closed':
      return PositionStatus.Closed;
  }
}

export function getActivityType(status: PositionStatus, order: Order, activityContent: string): string {
  if (status === PositionStatus.Open) {
    if (order.openings > 1 && order.placedCandidates > 0 && !order.latestTrackerActivity) return 'child_fill';
    if (order.numUnreviewedCandidates) return 'unreviewed_candidates';
    if (activityContent) return 'last_activity';
    if (order.macCreated) return 'client_requested';
  }
  if (status === PositionStatus.Working) {
    if (order.unsignedTimecardCount) return 'unsigned_timecards';
    if (order.incompleteQccId) return 'incomplete_review';
    if (isUpdateAssignmentDisplayed(order)) return 'update_assignment';

    const allActivities = getAllWorkingActivities(order).sort((one, two) => two.time.getTime() - one.time.getTime());

    if (allActivities.length > 0) {
      return allActivities[0].activityType;
    }
  }

  return '';
}

export function getAllWorkingActivities(order: Order): {activityType: string, time: Date}[] {
  const activities = [];

  if (order.lastReviewTime) {
    activities.push({activityType: 'last_review_time', time: order.lastReviewTime});
  }
  if (order.lastTimecardSignedTime) {
    activities.push({activityType: 'last_timecard_signed', time: order.lastTimecardSignedTime});
  }
  if (order.endDateUpdatedTime) {
    activities.push({activityType: 'end_date_updated', time: order.endDateUpdatedTime});
  }
  if (order.endOfAssignmentEventTime) {
    activities.push({activityType: order.endOfAssignmentEventType, time: order.endOfAssignmentEventTime});
  }

  return activities;
}

export function isUpdateAssignmentDisplayed(order: Order): boolean {
  const assignmentLength = differenceInDays(order.endDate, order.startDate);
  const daysBeforeEnd = differenceInDays(order.endDate, startOfToday());
  const hasBeenDismissed = order.endOfAssignmentEventType === EndOfAssignmentEventType.dismissed;

  return environment.flags.endOfAssignment
    && assignmentLength > 60
    && daysBeforeEnd < 31
    && daysBeforeEnd > 0
    && !hasBeenDismissed
    && !isEndDateUpdatePending(order)
    && !isUpdateAssignmentSnoozing(order);
}

export function isEndDateUpdatePending(order: Order): boolean {
  return order.endOfAssignmentEventType === EndOfAssignmentEventType.updated
    && (!order.endDateUpdatedTime || isBefore(order.endDateUpdatedTime, order.endOfAssignmentEventTime));
}

export function isUpdateAssignmentSnoozing(order: Order): boolean {
  const hasBeenUpdated = order.endDateUpdatedTime && isBefore(order.endOfAssignmentEventTime, order.endDateUpdatedTime);
  const hasBeenDeferred = order.endOfAssignmentEventType === EndOfAssignmentEventType.deferred;
  const daysBeforeEnd = differenceInDays(order.endDate, startOfToday());

  if (hasBeenUpdated) {
    return isSnoozing(order.endDateUpdatedTime, daysBeforeEnd);
  } else if (hasBeenDeferred) {
    return isSnoozing(order.endOfAssignmentEventTime, daysBeforeEnd);
  }

  return false;
}

function isSnoozing(snoozeEvent: Date, daysBeforeEnd: number) {
  const daysSinceEvent = differenceInDays(endOfToday(), snoozeEvent);

  return daysSinceEvent < 7 && daysBeforeEnd > 7;
}

export interface PositionSearchOptions {
  status?: PositionStatus;
  sort?: string;
  query?: string | null;
  filter?: string;
  needsAction?: boolean;
  ignoreTimecardActions?: boolean;
}

@Pipe({name: 'positionSearch'})
export class PositionSearchPipe implements PipeTransform {

  private static needsAction(order: Order, ignoreTimecards?: boolean): boolean {
    return !!(order.numUnreviewedCandidates
      || order.incompleteQccId
      || (!ignoreTimecards && order.unsignedTimecardCount)
      || isUpdateAssignmentDisplayed(order));
  }

  constructor(private Translate: TranslateService,
              private AppState: AppStateService) {
  }

  transform(orders: Order[], options: PositionSearchOptions): Order[] {
    return orders ? orders
      .filter(order => {
        if (!order.position || !order.position.trim()) return false;
        if (options) {
          const status = getPositionStatus(order);
          if (options.status && status !== options.status) return false;
          // Default to showing Open + Working
          if (!options.status && status !== PositionStatus.Open && status !== PositionStatus.Working) return false;
          if (options.needsAction && !PositionSearchPipe.needsAction(order)) return false;
          if (status === PositionStatus.Open && order.parentOrderId) return false;
          if (options.filter) {
            switch (options.filter) {
              case 'requested':
                if (order.maxTrackerStatus !== TrackerOrderStatus.created
                  && order.maxTrackerStatus !== TrackerOrderStatus.hasCandidates) return false;
                break;
              case 'reviewing':
                if (order.maxTrackerStatus !== TrackerOrderStatus.hasSubmittals) return false;
                break;
              case 'interviewing':
                if (order.maxTrackerStatus !== TrackerOrderStatus.interviewing) return false;
                break;
              case 'offer-extended':
                if (order.maxTrackerStatus !== TrackerOrderStatus.offerExtended) return false;
                break;
              case 'hired':
                if (order.maxTrackerStatus !== TrackerOrderStatus.offerAccepted) return false;
                break;
              case 'current-year':
                if (!startedOrEndedInCurrentYear(order)) return false;
                break;
              case 'current-quarter':
                if (!startedOrEndedInCurrentQuarter(order)) return false;
                break;
              case 'previous-year':
                if (!startedOrEndedInPreviousYear(order)) return false;
                break;
              case 'previous-quarter':
                if (!startedOrEndedInPreviousQuarter(order)) return false;
                break;
            }
          }
          if (options.query && !this.searchProfile(order, options.query)) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        let sort = 0;
        switch (options.sort) {
          case 'date-asc':
            sort = compareAsc(a.createDate, b.createDate);
            break;
          case 'date-desc':
            sort = compareDesc(a.createDate, b.createDate);
            break;
          case 'start-asc':
            sort = compareAsc(a.startDate, b.startDate);
            break;
          case 'start-date':
          case 'start-desc':
            sort = compareDesc(a.startDate, b.startDate);
            break;
          case 'end-date':
            sort = compareDesc(a.endDate, b.endDate);
            break;
          case 'end-date-asc':
            sort = compareAsc(a.endDate, b.endDate);
            break;
          case 'hired-date':
            sort = compareDesc(a.hiredDate, b.hiredDate);
            break;
          case 'action':
            if (PositionSearchPipe.needsAction(a, options.ignoreTimecardActions)) {
              sort = PositionSearchPipe.needsAction(b, options.ignoreTimecardActions) ? 0 : -1;
              break;
            } else if (PositionSearchPipe.needsAction(b, options.ignoreTimecardActions)) {
              sort = 1;
              break;
            }

          // Fall through to activity sort
          // noinspection FallThroughInSwitchStatementJS
          /* tslint:disable-next-line:no-switch-case-fall-through */
          case 'activity':
            const maxActivityForA = getPositionStatus(a) === PositionStatus.Working ? maxWorkingActivity(a) : maxOpenActivity(a);
            const maxActivityForB = getPositionStatus(b) === PositionStatus.Working ? maxWorkingActivity(b) : maxOpenActivity(b);

            sort = compareDesc(maxActivityForA, maxActivityForB);
            break;
        }
        if (!sort) return a.position.localeCompare(b.position);
        return sort;
      }) : [];
  }

  private searchProfile(order: Order, query: string): boolean {
    const fields = [
      order.position,
      // Search on status
      this.Translate.instant(`positions.tracker-status.${camelCaseToDashCase(TrackerOrderStatus[order.maxTrackerStatus])}`),
      // Search on working talent
      order.talent ? NameFormatPipe.apply(order.talent, this.AppState.locale$.getValue()) : ''
    ], terms = query ? query.split(' ') : [];

    /* tslint:disable-next-line:forin */
    for (const i in fields) {
      const field = (fields[i] || '').toLowerCase();
      if (terms.every((term) => field.indexOf(term.toLowerCase()) !== -1)) {
        return true;
      }
    }
    return false;
  }

}

function startedOrEndedInCurrentYear(order: Order): boolean {
  const currentYear = new Date().getFullYear();

  return (order.startDate.getFullYear() === currentYear) || (order.endDate.getFullYear() === currentYear);
}

function startedOrEndedInPreviousYear(order: Order): boolean {
  const previousYear = new Date().getFullYear() - 1;

  return (order.startDate.getFullYear() === previousYear) || (order.endDate.getFullYear() === previousYear);
}

function startedOrEndedInCurrentQuarter(order: Order): boolean {
  const today = new Date();

  return isSameQuarter(order.startDate, today) || isSameQuarter(order.endDate, today);
}

function startedOrEndedInPreviousQuarter(order: Order): boolean {
  const previousQuarterDate = getDateInPreviousQuarter(new Date());

  return isSameQuarter(order.startDate, previousQuarterDate) || isSameQuarter(order.endDate, previousQuarterDate);
}

function maxWorkingActivity(order: Order) {
  return max(
    order.lastReviewTime,
    order.lastTimecardSignedTime,
    order.endOfAssignmentEventTime,
    order.endDateUpdatedTime
  );
}

function maxOpenActivity(order: Order) {
  return max(
    order.latestTrackerActivity ? order.latestTrackerActivity.activityDate : 0,
    order.macCreated ? order.createDate : 0
  );
}
